/**
 * Per-level play statistics.
 *
 * Records lightweight aggregate counters about how the player interacts
 * with each level — totally separate from `Progress.ts`, which only
 * tracks best-replay completions. Useful for spotting frustration
 * hotspots: levels with many abandons, lots of deaths before a win,
 * long average time-to-win, etc.
 *
 * Tracked per `levelId`:
 *   - plays: how many times the level was loaded (live runs only;
 *     replay playback and editor test-mode are excluded by the caller).
 *   - wins / deaths / abandons / skips: outcome counters. "Abandon" =
 *     the player navigated to a different level mid-run without winning.
 *     "Skip" = the player advanced past a level via Next while it was
 *     already in progress without winning (treated as an abandon-flavour
 *     event but distinct so we can tell deliberate "I'll come back" apart
 *     from level-select switches).
 *   - totalTimeFrames: sum of every attempt's frame count, regardless of
 *     outcome. Divided by `plays` to get the average time per attempt.
 *   - timeToWinFramesSum / timeToDeathFramesSum: per-outcome sums for
 *     averages restricted to that outcome.
 *   - bestWinFrames / fastestDeathFrames: extremes.
 *   - lastPlayedAt: epoch ms of the most recent attempt's end.
 *
 * Storage: localStorage key `shk.levelStats.v1`, JSON-encoded
 * `{ [levelId]: LevelStatRecord }`. Writes are best-effort (quota
 * failures are swallowed). All getters are pure reads.
 */

const KEY = 'shk.levelStats.v1';

export interface LevelStatRecord {
  levelId: string;
  plays: number;
  wins: number;
  deaths: number;
  abandons: number;
  skips: number;
  restarts: number;
  totalTimeFrames: number;
  timeToWinFramesSum: number;
  timeToDeathFramesSum: number;
  bestWinFrames: number | null;
  fastestDeathFrames: number | null;
  longestAttemptFrames: number;
  lastPlayedAt: number;
}

type Store = Record<string, LevelStatRecord>;

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Store;
  } catch { return {}; }
}

function write(s: Store): void {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore quota */ }
}

function ensure(store: Store, levelId: string): LevelStatRecord {
  let rec = store[levelId];
  if (!rec) {
    rec = {
      levelId,
      plays: 0,
      wins: 0,
      deaths: 0,
      abandons: 0,
      skips: 0,
      restarts: 0,
      totalTimeFrames: 0,
      timeToWinFramesSum: 0,
      timeToDeathFramesSum: 0,
      bestWinFrames: null,
      fastestDeathFrames: null,
      longestAttemptFrames: 0,
      lastPlayedAt: 0,
    };
    store[levelId] = rec;
  }
  return rec;
}

/** Mark a fresh attempt as started. Bumps `plays` and `lastPlayedAt`. */
export function recordPlay(levelId: string): void {
  const s = read();
  const r = ensure(s, levelId);
  r.plays += 1;
  r.lastPlayedAt = Date.now();
  write(s);
}

/** Record a successful clear (`frames` = attempt length at the win). */
export function recordWin(levelId: string, frames: number): void {
  const s = read();
  const r = ensure(s, levelId);
  r.wins += 1;
  r.totalTimeFrames += frames;
  r.timeToWinFramesSum += frames;
  if (frames > r.longestAttemptFrames) r.longestAttemptFrames = frames;
  if (r.bestWinFrames === null || frames < r.bestWinFrames) r.bestWinFrames = frames;
  r.lastPlayedAt = Date.now();
  write(s);
}

/** Record a death. Many deaths can occur per attempt (checkpoint respawn
 *  doesn't rebuild the scene); each one is counted, but only the last
 *  death's frame count contributes to the time-to-death average via
 *  `recordAttemptEnd`. We split death-counting from attempt-ending so the
 *  caller can drive each independently. */
export function recordDeath(levelId: string, frames: number): void {
  const s = read();
  const r = ensure(s, levelId);
  r.deaths += 1;
  r.timeToDeathFramesSum += frames;
  if (r.fastestDeathFrames === null || frames < r.fastestDeathFrames) r.fastestDeathFrames = frames;
  r.lastPlayedAt = Date.now();
  write(s);
}

/** Player navigated away from the level mid-run without winning.
 *  `kind` distinguishes "abandon" (level-select / home / load another
 *  level), "skip" (Next pressed while still mid-attempt), and "restart"
 *  (same level reloaded via the Restart button or `r` key). All three
 *  fold the attempt's elapsed frames into the time totals. */
export function recordAbandon(levelId: string, frames: number, kind: 'abandon' | 'skip' | 'restart'): void {
  const s = read();
  const r = ensure(s, levelId);
  if (kind === 'skip') r.skips += 1;
  else if (kind === 'restart') r.restarts += 1;
  else r.abandons += 1;
  r.totalTimeFrames += frames;
  if (frames > r.longestAttemptFrames) r.longestAttemptFrames = frames;
  r.lastPlayedAt = Date.now();
  write(s);
}

/** Fold an attempt's elapsed frames into the totals without changing any
 *  outcome counter. Used when the attempt ended on the death overlay
 *  (the death is already counted by `recordDeath`) but the player then
 *  navigates away — we still want the time-spent-per-level aggregates
 *  to reflect the failed attempt. */
export function recordAttemptTime(levelId: string, frames: number): void {
  if (frames <= 0) return;
  const s = read();
  const r = ensure(s, levelId);
  r.totalTimeFrames += frames;
  if (frames > r.longestAttemptFrames) r.longestAttemptFrames = frames;
  r.lastPlayedAt = Date.now();
  write(s);
}

/** All records sorted by `levelId` ascending (numeric where both ids
 *  parse as numbers, otherwise lexical). Empty array when nothing has
 *  been recorded yet. */
export function listAll(): LevelStatRecord[] {
  const s = read();
  return Object.values(s).sort((a, b) => {
    const an = Number(a.levelId), bn = Number(b.levelId);
    const aNum = Number.isFinite(an), bNum = Number.isFinite(bn);
    if (aNum && bNum && an !== bn) return an - bn;
    return a.levelId.localeCompare(b.levelId);
  });
}

/** Lookup a single level's record. */
export function getRecord(levelId: string): LevelStatRecord | null {
  return read()[levelId] ?? null;
}

/** Wipe every recorded statistic. Idempotent. */
export function clearAll(): void {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** Format frames (60Hz) as M:SS.cc, matching scenes/game/Theme.ts. */
function fmtFrames(frames: number | null): string {
  if (frames === null) return '—';
  const totalCs = Math.round(frames * (100 / 60));
  const cs = totalCs % 100;
  const totalSec = Math.floor(totalCs / 100);
  const s = totalSec % 60;
  const m = Math.floor(totalSec / 60);
  return `${m}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
}

function avg(sum: number, n: number): number | null {
  if (n <= 0) return null;
  return sum / n;
}

/** Render every record as a CSV string with a header row. Suitable for
 *  pasting into a spreadsheet. Times are in M:SS.cc; raw frame counts
 *  are also included so external tools can re-derive seconds. */
export function toCsv(records: LevelStatRecord[]): string {
  const header = [
    'levelId',
    'plays',
    'wins',
    'deaths',
    'abandons',
    'skips',
    'restarts',
    'winRatePct',
    'avgAttemptTime',
    'avgTimeToWin',
    'avgTimeToDeath',
    'bestWin',
    'fastestDeath',
    'longestAttempt',
    'totalTimeFrames',
    'lastPlayedISO',
  ].join(',');
  const lines = records.map((r) => {
    const winRate = r.plays > 0 ? Math.round((r.wins / r.plays) * 1000) / 10 : 0;
    return [
      JSON.stringify(r.levelId),
      r.plays,
      r.wins,
      r.deaths,
      r.abandons,
      r.skips,
      r.restarts,
      winRate,
      fmtFrames(avg(r.totalTimeFrames, r.plays)),
      fmtFrames(avg(r.timeToWinFramesSum, r.wins)),
      fmtFrames(avg(r.timeToDeathFramesSum, r.deaths)),
      fmtFrames(r.bestWinFrames),
      fmtFrames(r.fastestDeathFrames),
      fmtFrames(r.longestAttemptFrames),
      r.totalTimeFrames,
      r.lastPlayedAt > 0 ? new Date(r.lastPlayedAt).toISOString() : '',
    ].join(',');
  });
  return [header, ...lines].join('\n');
}

/** Render a compact human-readable text block for the same records. */
export function toText(records: LevelStatRecord[]): string {
  if (records.length === 0) return 'No level statistics recorded yet.';
  const blocks = records.map((r) => {
    const winRate = r.plays > 0 ? ((r.wins / r.plays) * 100).toFixed(1) : '0.0';
    return [
      `[${r.levelId}]`,
      `  plays=${r.plays}  wins=${r.wins}  deaths=${r.deaths}  abandons=${r.abandons}  skips=${r.skips}  restarts=${r.restarts}  winRate=${winRate}%`,
      `  avgAttempt=${fmtFrames(avg(r.totalTimeFrames, r.plays))}  avgWin=${fmtFrames(avg(r.timeToWinFramesSum, r.wins))}  avgDeath=${fmtFrames(avg(r.timeToDeathFramesSum, r.deaths))}`,
      `  bestWin=${fmtFrames(r.bestWinFrames)}  fastestDeath=${fmtFrames(r.fastestDeathFrames)}  longestAttempt=${fmtFrames(r.longestAttemptFrames)}`,
      `  lastPlayed=${r.lastPlayedAt > 0 ? new Date(r.lastPlayedAt).toISOString() : '—'}`,
    ].join('\n');
  });
  return blocks.join('\n\n');
}

/** Format helpers re-exported for the modal. */
export const fmt = { frames: fmtFrames, avg };
